/**
 * RulesPanelContainer — data + open-state wiring for the pipeline Rules &
 * automation panel (SE-5506).
 *
 * Subscribes to the panel reactive var (so any toolbar can open it), fetches
 * the board's rules + stages + tags + valid outcomes (all skipped while closed),
 * groups the rules into Automations vs Rules/Guards, and owns the builder's
 * open/edit state. The builder is CONDITIONALLY mounted, so each open seeds a
 * fresh form with no reset effect. Enable/pause + delete run through
 * `useAutomationRuleCrud` (refetch-backed); per-rule in-flight ids gate the row
 * controls.
 * @remarks Container component — owns queries, grouping, and CRUD actions.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  type ListKanbanTagsQuery,
  type PipelineAutomationRuleFragmentFragment,
  type PipelineAutomationRulesQuery,
  useGetKanbanBoardQuery,
  useListKanbanPhasesQuery,
  useListKanbanTagsQuery,
  usePipelineAutomationRulesQuery,
} from "@/generated/graphql";
import { useToastNotification } from "@/hooks/useToastNotification";

import { AUTOMATION_RULES_PAGE_SIZE, MESSAGES } from "../../../constants";
import { useAutomationRuleCrud } from "../../../hooks/useAutomationRuleCrud";
import { useRulesPanelState } from "../../../hooks/useRulesPanelState";
import type {
  AutomationRuleKind,
  RuleStageOption,
  RuleTagOption,
} from "../../../types/automationRules";
import { groupAutomationRules } from "../../../utils/automationRuleKind";
import AutomationRuleBuilder from "../AutomationRuleBuilder";
import RulesPanelView from "./RulesPanelView";

/** Global tag page size (the tag list query is not board-scoped). */
const TAGS_PAGE_SIZE = 100;

/** Relay pageInfo shape shared by both paginated connections. */
interface ConnectionPageInfo {
  readonly hasNextPage: boolean;
  readonly endCursor?: string | null;
}

/**
 * Collapse a list to one entry per `id`, keeping the first occurrence and
 * preserving order. Guards the read boundary against a relay/updateQuery merge
 * ever holding two edges for the same entity (SE-5404 pattern).
 * @param items - The items to de-duplicate.
 * @returns Items with at most one entry per id, original order preserved.
 */
const dedupeById = <T extends { readonly id: string }>(
  items: readonly T[]
): readonly T[] =>
  items.filter(
    (item, index) => items.findIndex(other => other.id === item.id) === index
  );

/**
 * Merge one fetched rules page onto the accumulated result by concatenating
 * edges and adopting the newest page's `pageInfo`/`totalCount`.
 * @param previous - The result accumulated so far.
 * @param next - The freshly fetched page (undefined on an empty refetch).
 * @returns The combined query result.
 */
const mergeRulesPages = (
  previous: PipelineAutomationRulesQuery,
  next: PipelineAutomationRulesQuery | undefined
): PipelineAutomationRulesQuery => {
  if (!next) return previous;
  return {
    ...next,
    pipelineAutomationRules: {
      ...next.pipelineAutomationRules,
      edges: [
        ...previous.pipelineAutomationRules.edges,
        ...next.pipelineAutomationRules.edges,
      ],
    },
  };
};

/**
 * Merge one fetched tags page onto the accumulated result.
 * @param previous - The result accumulated so far.
 * @param next - The freshly fetched page (undefined on an empty refetch).
 * @returns The combined query result.
 */
const mergeTagsPages = (
  previous: ListKanbanTagsQuery,
  next: ListKanbanTagsQuery | undefined
): ListKanbanTagsQuery => {
  if (!next) return previous;
  return {
    ...next,
    listKanbanTags: {
      ...next.listKanbanTags,
      edges: [...previous.listKanbanTags.edges, ...next.listKanbanTags.edges],
    },
  };
};

/**
 * Drive a query's next-page fetch until `hasNextPage` is false, so the panel
 * always holds the WHOLE rule/tag set — a rule or tag past the first page must
 * still be viewable, editable, and deletable. Each distinct cursor is requested
 * at most once (guarded by a ref) so rapid re-renders cannot double-append a
 * page, and the ledger resets whenever pagination goes idle.
 * @param pageInfo - The connection's current relay pageInfo.
 * @param fetchNext - Fetches + merges the page after the given cursor.
 * @param skip - When true, pagination is idle (panel closed / no board).
 */
const useFetchAllPages = (
  pageInfo: ConnectionPageInfo | undefined,
  fetchNext: (after: string) => Promise<unknown>,
  skip: boolean
): void => {
  const requestedCursors = useRef<Set<string>>(new Set());
  const hasNextPage = pageInfo?.hasNextPage ?? false;
  const endCursor = pageInfo?.endCursor ?? null;

  useEffect(() => {
    if (skip) {
      requestedCursors.current = new Set();
      return;
    }
    if (!hasNextPage || !endCursor) return;
    if (requestedCursors.current.has(endCursor)) return;

    requestedCursors.current.add(endCursor);
    void fetchNext(endCursor);
  }, [skip, hasNextPage, endCursor, fetchNext]);
};

/**
 * Container for the Rules & automation panel.
 * @returns The wired {@link RulesPanelView} plus the (conditional) builder.
 */
const RulesPanelContainer = () => {
  const toast = useToastNotification();
  const { state, close } = useRulesPanelState();
  const boardId = state.kanbanBoardId ?? "";
  const skip = !state.open || !boardId;

  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<
    PipelineAutomationRuleFragmentFragment | undefined
  >(undefined);
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());

  const rulesQuery = usePipelineAutomationRulesQuery({
    variables: { kanbanBoardId: boardId, first: AUTOMATION_RULES_PAGE_SIZE },
    skip,
    fetchPolicy: "cache-and-network",
  });
  const phasesQuery = useListKanbanPhasesQuery({
    variables: { kanbanBoardId: boardId },
    skip,
  });
  const tagsQuery = useListKanbanTagsQuery({
    variables: { first: TAGS_PAGE_SIZE },
    skip,
  });
  const boardQuery = useGetKanbanBoardQuery({
    variables: { id: boardId },
    skip,
  });

  // Walk every remaining page of BOTH connections so the panel holds the whole
  // set — a rule/tag past the first page must stay viewable, editable, and
  // deletable rather than silently capped at the first page.
  const { fetchMore: rulesFetchMore } = rulesQuery;
  const fetchNextRules = useCallback(
    (after: string) =>
      rulesFetchMore({
        variables: { after },
        updateQuery: (previous, { fetchMoreResult }) =>
          mergeRulesPages(previous, fetchMoreResult),
      }),
    [rulesFetchMore]
  );
  const { fetchMore: tagsFetchMore } = tagsQuery;
  const fetchNextTags = useCallback(
    (after: string) =>
      tagsFetchMore({
        variables: { after },
        updateQuery: (previous, { fetchMoreResult }) =>
          mergeTagsPages(previous, fetchMoreResult),
      }),
    [tagsFetchMore]
  );

  const { setRuleEnabled, deleteRule } = useAutomationRuleCrud(boardId);

  const rules = useMemo(
    () =>
      // De-dupe by id: an accumulated multi-page result could otherwise hold two
      // edges for one rule (distinct cursors) if a page were ever appended
      // twice, which would double a row in the grouped list.
      dedupeById(
        (rulesQuery.data?.pipelineAutomationRules.edges ?? []).map(
          edge => edge.node
        )
      ),
    [rulesQuery.data]
  );
  const grouped = useMemo(() => groupAutomationRules(rules), [rules]);

  const stages = useMemo<readonly RuleStageOption[]>(
    () =>
      [...(phasesQuery.data?.listKanbanPhases ?? [])]
        .sort((a, b) => a.position - b.position)
        .map(phase => ({
          id: phase.id,
          name: phase.name,
          isTerminal: phase.isTerminal,
        })),
    [phasesQuery.data]
  );
  const tags = useMemo<readonly RuleTagOption[]>(
    () =>
      dedupeById(
        (tagsQuery.data?.listKanbanTags.edges ?? []).map(edge => ({
          id: edge.node.id,
          name: edge.node.name,
        }))
      ),
    [tagsQuery.data]
  );
  const validOutcomes = useMemo(
    () => boardQuery.data?.getKanbanBoard?.validOutcomes ?? [],
    [boardQuery.data]
  );

  const lookups = useMemo(
    () => ({
      phaseName: (id: string | null | undefined) =>
        stages.find(stage => stage.id === id)?.name ?? "…",
      tagName: (id: string | null | undefined) =>
        tags.find(tag => tag.id === id)?.name ?? "…",
    }),
    [stages, tags]
  );

  const markBusy = useCallback((id: string, active: boolean) => {
    setBusyIds(prev =>
      active
        ? new Set([...prev, id])
        : new Set([...prev].filter(existing => existing !== id))
    );
  }, []);

  /*
   * The panel's two creation entry points differ ONLY in the Kind the builder
   * opens on — there is one builder and one create mutation behind both. The
   * seeded kind is a starting point, not a lock: the builder's Kind toggle
   * stays live, so a user who picks the wrong button is one tap from the other.
   */
  const [newRuleKind, setNewRuleKind] =
    useState<AutomationRuleKind>("automation");

  const openBuilderFor = useCallback((kind: AutomationRuleKind) => {
    setNewRuleKind(kind);
    setEditingRule(undefined);
    setBuilderOpen(true);
  }, []);

  const handleNewAutomation = useCallback(
    () => openBuilderFor("automation"),
    [openBuilderFor]
  );
  const handleNewGuard = useCallback(
    () => openBuilderFor("guard"),
    [openBuilderFor]
  );

  const handleEdit = useCallback(
    (rule: PipelineAutomationRuleFragmentFragment) => {
      setEditingRule(rule);
      setBuilderOpen(true);
    },
    []
  );

  const handleCloseBuilder = useCallback(() => {
    setBuilderOpen(false);
    setEditingRule(undefined);
  }, []);

  // Closing the drawer must also tear down the builder — otherwise its open
  // state survives the close and the builder resurfaces the next time the panel
  // is opened (the builder is gated on the board id, which the panel restores).
  const handleClosePanel = useCallback(() => {
    setBuilderOpen(false);
    setEditingRule(undefined);
    close();
  }, [close]);

  const handleToggleEnabled = useCallback(
    async (
      rule: PipelineAutomationRuleFragmentFragment,
      isEnabled: boolean
    ) => {
      markBusy(rule.id, true);
      try {
        await setRuleEnabled(rule.id, isEnabled);
      } catch {
        toast.show({ action: "danger", description: MESSAGES.ERROR.GENERIC });
      } finally {
        markBusy(rule.id, false);
      }
    },
    [markBusy, setRuleEnabled, toast]
  );

  const handleDelete = useCallback(
    async (rule: PipelineAutomationRuleFragmentFragment) => {
      markBusy(rule.id, true);
      try {
        await deleteRule(rule.id);
        toast.show({
          action: "success",
          description: MESSAGES.SUCCESS.DELETED,
        });
      } catch {
        toast.show({ action: "danger", description: MESSAGES.ERROR.GENERIC });
      } finally {
        markBusy(rule.id, false);
      }
    },
    [markBusy, deleteRule, toast]
  );

  const loading =
    rulesQuery.loading || phasesQuery.loading || boardQuery.loading;
  const isError = Boolean(rulesQuery.error);

  // Side effects run after all definitions (code-organization/enforce-statement-order):
  // auto-advance both connections through every remaining page.
  useFetchAllPages(
    rulesQuery.data?.pipelineAutomationRules.pageInfo,
    fetchNextRules,
    skip
  );
  useFetchAllPages(
    tagsQuery.data?.listKanbanTags.pageInfo,
    fetchNextTags,
    skip
  );

  return (
    <>
      <RulesPanelView
        isOpen={state.open}
        onClose={handleClosePanel}
        loading={loading}
        isError={isError}
        automations={grouped.automations}
        guards={grouped.guards}
        lookups={lookups}
        busyIds={busyIds}
        pipelineName={boardQuery.data?.getKanbanBoard?.name ?? ""}
        onNewAutomation={handleNewAutomation}
        onNewGuard={handleNewGuard}
        onToggleEnabled={handleToggleEnabled}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      {builderOpen && boardId ? (
        <AutomationRuleBuilder
          isOpen={builderOpen}
          boardId={boardId}
          editingRule={editingRule}
          initialKind={newRuleKind}
          stages={stages}
          tags={tags}
          validOutcomes={validOutcomes}
          onClose={handleCloseBuilder}
        />
      ) : null}
    </>
  );
};

RulesPanelContainer.displayName = "RulesPanelContainer";

export default memo(RulesPanelContainer);
